import { Fragment } from "react";
import { Beaker, FlaskConical, LucideIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type FeatureStatus = "experimental" | "research-preview";

interface FeatureStatusBadgeProps {
	featureName: string;
	status?: FeatureStatus;
	className?: string;
}

interface StatusConfig {
	label: string;
	icon: LucideIcon;
	pillClassName: string;
	popoverIconClassName: string;
	currentDotClassName: string;
	headlineSuffix: string;
	body: string;
}

const STATUS_CONFIG: Record<FeatureStatus, StatusConfig> = {
	experimental: {
		label: "Experimental",
		icon: Beaker,
		pillClassName: cn(
			"bg-amber-100 text-amber-800 hover:bg-amber-200",
			"dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/60",
			"focus-visible:ring-amber-500",
		),
		popoverIconClassName: "text-amber-700 dark:text-amber-300",
		currentDotClassName: "bg-amber-500",
		headlineSuffix: "is Experimental",
		body: "This feature is in early development and is being actively tested and iterated on. Expect frequent changes as we refine the behavior and experience based on real usage.",
	},
	"research-preview": {
		label: "Preview",
		icon: FlaskConical,
		pillClassName: cn(
			"bg-violet-100 text-violet-800 hover:bg-violet-200",
			"dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/60",
			"focus-visible:ring-violet-500",
		),
		popoverIconClassName: "text-violet-700 dark:text-violet-300",
		currentDotClassName: "bg-violet-500",
		headlineSuffix: "is in Preview",
		body: "This feature is fully available and safe to use. We're still actively validating it and refining the experience based on real usage, so expect occasional changes.",
	},
};

type LifecycleStage = { key: FeatureStatus | "live"; label: string };

const LIFECYCLE_STAGES: ReadonlyArray<LifecycleStage> = [
	{ key: "experimental", label: "Experimental" },
	{ key: "research-preview", label: "Preview" },
	{ key: "live", label: "Live" },
];

interface LifecycleTimelineProps {
	currentStatus: FeatureStatus;
	currentDotClassName: string;
}

const LifecycleTimeline = ({ currentStatus, currentDotClassName }: LifecycleTimelineProps) => {
	const currentIndex = LIFECYCLE_STAGES.findIndex(stage => stage.key === currentStatus);

	return (
		<div className="flex flex-col">
			<div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
				Lifecycle
			</div>
			{LIFECYCLE_STAGES.map((stage, index) => {
				const isCurrent = index === currentIndex;
				const isPast = index < currentIndex;
				const isLast = index === LIFECYCLE_STAGES.length - 1;

				return (
					<Fragment key={stage.key}>
						<div className="flex items-center gap-2.5">
							<div
								className={cn(
									"size-2 rounded-full shrink-0",
									isCurrent
										? currentDotClassName
										: isPast
											? "bg-muted-foreground"
											: "bg-transparent border border-muted-foreground/40",
								)}
							/>
							<span
								className={cn(
									"text-xs",
									isCurrent ? "font-semibold text-foreground" : "text-muted-foreground",
								)}
							>
								{stage.label}
								{isCurrent && (
									<span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
										(current)
									</span>
								)}
							</span>
						</div>
						{!isLast && <div className="ml-[3px] h-2 w-px bg-border" />}
					</Fragment>
				);
			})}
		</div>
	);
};

export const FeatureStatusBadge = ({
	featureName,
	status = "research-preview",
	className,
}: FeatureStatusBadgeProps) => {
	const config = STATUS_CONFIG[status];
	const Icon = config.icon;

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label={`${featureName} ${config.headlineSuffix}. Click for details.`}
					className={cn(
						"inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
						"w-fit self-start",
						config.pillClassName,
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
						className,
					)}
				>
					<Icon className="size-3.5" />
					<span>{config.label}</span>
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-80">
				<div className="flex flex-col gap-3">
					<div className={cn("flex items-center gap-2", config.popoverIconClassName)}>
						<Icon className="size-4" />
						<span className="text-sm font-semibold">
							{featureName} {config.headlineSuffix}
						</span>
					</div>
					<p className="text-sm text-muted-foreground">{config.body}</p>
					<div className="border-t pt-3">
						<LifecycleTimeline currentStatus={status} currentDotClassName={config.currentDotClassName} />
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};
